import React from 'react';
import { constants } from '@clubmed/utils';
import { ICONS } from 'Components/utils/icon/icons';
import { BeTransportDetails } from 'Components/widgets/be-transport-details/BeTransportDetails';
import { GetParticipantsActivities } from 'Components/widgets/stay-summary-ticket/GetParticipantsActivities';
import { StaySummaryTicketUpsell } from 'Components/widgets/stay-summary-ticket/StaySummaryTicketUpsell';
import { getTransferLabels } from 'Components/widgets/stay-summary-ticket/StaySummaryTicket.utils';
import { asProposalPrice } from 'Src/types/cast-helpers';
import { getKeyValue, t } from 'Src/i18n';
import { getFormattedArrivalDate, getFormattedDepartureDate } from 'Utils/proposal/proposalUtils';
import { ChildcareSummary } from 'Components/widgets/childcare-summary/ChildcareSummary';
import {
  asLegacyJsxComponent,
  asProposalFeeTaxes,
  makeViewProps,
  type ProposalFeeTaxesMap,
} from 'Src/types/view-helpers';
import { formatAmountForLocale } from 'Utils/currency-config-for-locale/currencyConfigForLocale';
import { displayActivitiesAndEquipmentsNames } from 'Utils/activity/activity.utils';
import { displayChildcaresNames } from 'Utils/childcare/childcare.utils';
import { roomQuantity } from 'Utils/accommodation/accommodation.utils';
import { getNumberOfAdultsAndChildren } from 'Utils/participants/participants.utils';
import { SelectedRate } from 'Components/widgets/bottom-navbar-layer/SelectedRate';
import { classnames } from 'Utils/classnames/classnames.utils';
import { Icon } from 'Components/frameworks/icon/Icon';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';

const GetParticipantsActivitiesWidget = asLegacyJsxComponent(GetParticipantsActivities);
const StaySummaryTicketUpsellWidget = asLegacyJsxComponent(StaySummaryTicketUpsell);
const ChildcareSummaryWidget = asLegacyJsxComponent(ChildcareSummary);
const { FLEX_RATES, OFFERS } = constants;
type Props = {
  accommodationSummary: ComponentType;
  i18n: CMSContent;
  locale: string;
  selectedProposal: Proposal;
  isJoyViewOrUrbanOasisAllInclusive: boolean;
  isUrbanOasisFoodAndBeverage: boolean;
};
export const getOffersAndDiscounts = (proposalDiscounts: Array<ProposalDiscount>) => {
  const discounts: Array<ProposalDiscount> = proposalDiscounts.filter(
    (discount) => discount.offerId === OFFERS.PROMO_CODE,
  );
  const offers: Array<ProposalDiscount> = proposalDiscounts.filter(
    (discount) => discount.offerId !== OFFERS.PROMO_CODE,
  );
  return {
    offers,
    discounts,
  };
};
export const getPriceDetail = ({
  selectedProposal,
  priceWithDonation,
  showPriceDetailLines,
  promoCode,
  insurance,
}: {
  selectedProposal: Proposal;
  priceWithDonation: ProposalPriceWithDonation;
  showPriceDetailLines: boolean;
  promoCode?: Node;
  insurance?: Node;
}) => {
  const insuranceValue = () => {
    if (
      selectedProposal.price.insurance === undefined ||
      selectedProposal.price?.insurance?.value === null
    ) {
      return null;
    }

    return selectedProposal.price?.insurance?.formattedValue;
  };

  const taxes = asProposalFeeTaxes(
    selectedProposal.price?.fees?.reduce((acc, { type, amount }) => {
      acc[type] = amount.formattedValue;
      return acc;
    }, {} as ProposalFeeTaxesMap),
  );
  const price = priceWithDonation.withDonation;
  const totalPrice = selectedProposal.price.total.formattedValue;
  const selectedRate =
    price && price.hasFlexRates && price.selectedRate ? (
      <div
        className={classnames('mt-5 rounded-pill px-10 py-5', {
          'bg-green': price.selectedRate === FLEX_RATES.STANDARD,
          'bg-sienna':
            price.selectedRate === FLEX_RATES.NRR || price.selectedRate === FLEX_RATES.FLR,
        })}
      >
        <SelectedRate price={asProposalPrice(price)} />
      </div>
    ) : null;
  const priceDetail = [
    {
      iconName: ICONS.DISHES,
      dataTestId: 'dishes-button',
      topLeft: t('booking.accommodation.packageContent.allInclusiveReassurance.cuisine.message'),
      topRight: t('common.included'),
      children: (
        <p className="mb-10 text-14">
          {t(
            'booking.accommodation.packageContent.allInclusiveReassuranceCuisineDetailedDescription',
          )}
        </p>
      ),
      isFirstItem: true,
      trackingData: {
        event: TRACK_EVENT_NAMES.CLICK_RECEIPT_TICKET,
        payload: {
          detail_click: 'price_details_receipt_food',
        },
      },
    },
    {
      iconName: ICONS.BAR,
      dataTestId: 'bar-button',
      topLeft: t('booking.accommodation.packageContent.allInclusiveReassurance.bar.message'),
      topRight: t('common.included'),
      children: (
        <p className="mb-10 text-14">
          {t('booking.accommodation.packageContent.allInclusiveReassuranceBarDetailedDescription')}
        </p>
      ),
      trackingData: {
        event: TRACK_EVENT_NAMES.CLICK_RECEIPT_TICKET,
        payload: {
          detail_click: 'price_details_receipt_beverage',
        },
      },
    },
    {
      iconName: ICONS.TENNIS,
      dataTestId: 'price-activities-button',
      topLeft: t(
        'booking.accommodation.packageContent.allInclusiveReassurance.sportAndActivities.message',
      ),
      topRight: t('common.included'),
      children: (
        <p className="mb-10 text-14">
          {t(
            'booking.accommodation.packageContent.allInclusiveReassuranceSportAndActivitiesDetailedDescription',
          )}
        </p>
      ),
      trackingData: {
        event: TRACK_EVENT_NAMES.CLICK_RECEIPT_TICKET,
        payload: {
          detail_click: 'price_details_receipt_activities',
        },
      },
    },
    {
      iconName: ICONS.ENTERTAINMENT,
      dataTestId: 'shows-button',
      topLeft: t(
        'booking.accommodation.packageContent.allInclusiveReassurance.entertainment.message',
      ),
      topRight: t('common.included'),
      children: (
        <p className="mb-10 text-14">
          {t(
            'booking.accommodation.packageContent.allInclusiveReassuranceEntertainmentDetailedDescription',
          )}
        </p>
      ),
      trackingData: {
        event: TRACK_EVENT_NAMES.CLICK_RECEIPT_TICKET,
        payload: {
          detail_click: 'price_details_receipt_shows',
        },
      },
    },
    {
      iconName: ICONS.WIFI,
      topLeft: t('booking.accommodation.packageContent.allInclusiveReassurance.wifiAccess.message'),
      topRight: t('common.included'),
    },
    {
      isNull: !getKeyValue(t('booking.priceDetails.packagePrice.displayMedicalInsurance')),
      iconName: ICONS.INSURANCE,
      topLeft: t('booking.priceDetails.packagePrice.medicalInsurance'),
      topRight: t('common.included'),
    },
    {
      isNull: !promoCode,
      component: promoCode,
    },
    {
      isNull: !insurance,
      component: insurance,
    },
    {
      dataTestId: 'fees-button',
      isNull: !taxes?.SUBSCRIPTION,
      topLeft: t('booking.priceDetails.packagePrice.fees.annualMembershipFeesLabel'),
      topRight: <p className="font-bold text-sienna">+ {taxes?.SUBSCRIPTION}</p>,
      children: (
        <p className="text-14">{t('booking.priceDetails.packagePrice.annualFeesInformation')}</p>
      ),
      trackingData: {
        event: TRACK_EVENT_NAMES.CLICK_RECEIPT_TICKET,
        payload: {
          detail_click: 'price_details_receipt_fees',
        },
      },
    },
    {
      dataTestId: 'taxes-button',
      isNull: !taxes?.PORT_TAXES,
      topLeft: t('booking.priceDetails.packagePrice.fees.portTaxesLabel'),
      topRight: <p className="font-bold text-sienna">+ {taxes?.PORT_TAXES}</p>,
      children: (
        <p className="text-14">
          {t('booking.priceDetails.packagePrice.fees.portTaxesDescription')}
        </p>
      ),
      trackingData: {
        event: TRACK_EVENT_NAMES.CLICK_RECEIPT_TICKET,
        payload: {
          detail_click: 'price_details_receipt_taxes',
        },
      },
    },
    {
      isNull: insuranceValue() === null,
      topLeft: t('booking.insurance.insuranceLabel'),
      topRight: <p className="font-bold text-sienna">+ {insuranceValue()}</p>,
    },
    {
      dataTestId: 'touristic-taxes-button',
      isNull: !taxes?.TOURISTIC_TAXES,
      topLeft: t('booking.priceDetails.packagePrice.fees.touristicTaxesLabel'),
      topRight: <p className="font-bold text-sienna">+ {taxes?.TOURISTIC_TAXES}</p>,
      children: (
        <p className="text-14">
          {t('booking.priceDetails.packagePrice.fees.touristicTaxesDescription')}
        </p>
      ),
      trackingData: {
        event: TRACK_EVENT_NAMES.CLICK_RECEIPT_TICKET,
        payload: {
          detail_click: 'price_details_receipt_touristic_taxes',
        },
      },
    },
    {
      isLastItem: true,
      topLeft: <p className="font-bold">{t('booking.priceDetails.packagePrice.totalPrice')}</p>,
      topRight: <p className="text-18 font-bold">{totalPrice}</p>,
      bottomRight: selectedRate,
      bottomLeft: getKeyValue(
        t('booking.priceDetails.packagePrice.fees.displayTaxesAndFeesIncludedMention'),
      ) && (
        <p className="font-normal">
          {t('booking.priceDetails.packagePrice.fees.taxesAndFeesIncludedLabel')}
        </p>
      ),
      trackingData: {
        event: TRACK_EVENT_NAMES.CLICK_RECEIPT_TICKET,
        payload: {
          detail_click: 'price_details_receipt_touristic_taxes',
        },
      },
    },
  ];

  if (!showPriceDetailLines) {
    return [priceDetail.pop()];
  }

  return priceDetail;
};
export const getSummaryViewLinesByProposal = ({
  accommodationSummary,
  locale,
  selectedProposal,
  isJoyViewOrUrbanOasisAllInclusive,
  isUrbanOasisFoodAndBeverage,
}: Props) => {
  const { accommodations, attendees, duration, price } = selectedProposal;
  const quantityOfAdults = attendees.adults.length;
  const quantityOfChildren = attendees.children.length;
  const childcares = selectedProposal?.additionalServices?.childcares;
  const activities = selectedProposal?.additionalServices?.activities?.filter((obj) => {
    return obj.pricingInfo.isPartiallyRented === true;
  });
  const equipments = selectedProposal?.additionalServices?.rentals?.filter((rental) => {
    return rental.pricingInfo.isPartiallyRented === true;
  });
  const transfers = selectedProposal?.additionalServices?.transfers;
  const transferUpsell = transfers?.filter((obj) => {
    return obj.pricingInfo.isFullyRented === true;
  });
  const isTransferIncluded =
    selectedProposal.transports?.inbound?.transferIncluded ||
    selectedProposal.transports?.outbound?.transferIncluded ||
    transfers?.some((obj) => obj.pricingInfo.isIncluded === true) ||
    false;
  const sumPriceAllActivities =
    activities
      .map((equipment) => equipment.pricingInfo?.rentedAmountWithoutDiscount?.value)
      .reduce((acc, currentValue) => acc + currentValue, 0) +
    equipments
      .map((equipment) => equipment.pricingInfo?.rentedAmountWithoutDiscount?.value)
      .reduce((acc, currentValue) => acc + currentValue, 0);
  const hasTransports = selectedProposal.transports;
  const hasActivities = activities.length > 0;
  const hasEquipments = equipments.length > 0;
  const childcaresUpsell = childcares.filter(
    (childcare) => childcare.pricingInfo.isPartiallyRented === true,
  );
  const hasTransfer = transferUpsell.length > 0 || hasTransports;
  const accommodationsIds = accommodations.map((accommodation) => accommodation.id);
  const selectedAccommodations = accommodations.filter((acc) => accommodationsIds.includes(acc.id));
  const bedroomsNames =
    selectedAccommodations && selectedAccommodations.length > 1
      ? selectedAccommodations &&
        selectedAccommodations.map((el) => el.label).join(` ${t('common.and')} `)
      : selectedAccommodations[0]?.label;
  const transferLabels = getTransferLabels(selectedProposal);
  const nightsDuration =
    duration > 1
      ? t('booking.confirmation.nights', {
          duration,
        })
      : `${duration} ${t('booking.confirmation.night')}`;
  const rooms =
    roomQuantity(accommodations)[0] > 1 || selectedAccommodations.length > 1
      ? t('booking.confirmation.rooms')
      : t('booking.confirmation.room');
  const childcareIncludedOrOnDemand = childcaresUpsell.length > 0 && (
    <StaySummaryTicketUpsellWidget
      transferUpsell={transferUpsell}
      childcares={childcares}
      price={price}
      isTransferIncluded={isTransferIncluded}
    />
  );
  const lines = [
    {
      iconName: ICONS.DATE,
      topLeft: t('booking.common.dates'),
      bottomLeft: `${getFormattedArrivalDate(selectedProposal).slice(0, 6)} -
            ${getFormattedDepartureDate(selectedProposal).slice(0, 6)}`,
      bottomRight: nightsDuration,
      isFirstItem: true,
    },
    {
      iconName: ICONS.PEOPLE_DOUBLE,
      topLeft: t('booking.common.participants'),
      bottomLeft: getNumberOfAdultsAndChildren(quantityOfChildren, quantityOfAdults),
    },
    {
      iconName: ICONS.ACCOMODATIONS,
      dataTestId: 'accommodation-button',
      bottomLeft: bedroomsNames,
      children: accommodationSummary,
      labelGA: 'receipt_room',
      topLeft: rooms,
      trackingData: {
        event: TRACK_EVENT_NAMES.CLICK_RECEIPT_TICKET,
        payload: {
          detail_click: 'receipt_room',
        },
      },
    },
    {
      iconName: ICONS.TRANSFER,
      isNull: !hasTransfer,
      bottomLeft: transferLabels,
      labelGA: 'receipt_transfert',
      topLeft: t('common.transport.transfer.withTransferLabel'),
      topRight: (transferUpsell.length > 0 || isTransferIncluded) && (
        <StaySummaryTicketUpsellWidget
          transferUpsell={transferUpsell}
          isTransferIncluded={isTransferIncluded}
          price={price}
          childcares={childcares}
        />
      ),
      trackingData: {
        event: TRACK_EVENT_NAMES.CLICK_RECEIPT_TICKET,
        payload: {
          detail_click: 'receipt_transfert',
        },
      },
    },
    {
      iconName: ICONS.TRANSPORT,
      dataTestId: 'transport-button',
      isNull: !hasTransports,
      bottomLeft: t('booking.confirmation.inboundAndOutbound'),
      topLeft: t('common.transport.withTransportLabel'),
      labelGA: 'receipt_transport',
      children: (
        <div className="mb-15">
          <BeTransportDetails transport={selectedProposal?.transports} withoutTitle />
        </div>
      ),
      trackingData: {
        event: TRACK_EVENT_NAMES.CLICK_RECEIPT_TICKET,
        payload: {
          detail_click: 'receipt_transport',
        },
      },
    },
    {
      iconName: ICONS.KIDS,
      isNull: childcaresUpsell.length === 0,
      bottomLeft: displayChildcaresNames(selectedProposal, childcaresUpsell),
      children: (
        <ChildcareSummaryWidget
          {...makeViewProps<any>({ childcares: childcaresUpsell, proposal: selectedProposal })}
        />
      ),
      dataTestId: 'childcare-button',
      topLeft: t('common.price.childcareLabel'),
      topRight: childcareIncludedOrOnDemand,
      trackingData: {
        event: TRACK_EVENT_NAMES.CLICK_RECEIPT_TICKET,
        payload: {
          detail_click: 'receipt_childcare',
        },
      },
    },
    {
      iconName: ICONS.TENNIS,
      isNull: !hasActivities && !hasEquipments,
      bottomLeft: displayActivitiesAndEquipmentsNames(
        hasActivities,
        hasEquipments,
        activities,
        equipments,
      ),
      bottomRight: (
        <p className="text-orange">
          {'+ ' +
            formatAmountForLocale(sumPriceAllActivities, locale, undefined, {
              precision: 2,
            })}
        </p>
      ),
      children: (
        <div className="mt-30">
          <GetParticipantsActivitiesWidget
            proposal={selectedProposal}
            participants={selectedProposal.attendees.adults}
            isChild={false}
          />
          <GetParticipantsActivitiesWidget
            proposal={selectedProposal}
            participants={selectedProposal.attendees.children}
            isChild
          />
        </div>
      ),
      dataTestId: 'activities-button',
      topLeft: t('common.activities.label'),
      topRight: (
        <p className="text-14 font-bold text-orange">{t('booking.confirmation.onDemand')}</p>
      ),
      trackingData: {
        event: TRACK_EVENT_NAMES.CLICK_RECEIPT_TICKET,
        payload: {
          detail_click: 'receipt_activities',
        },
      },
    },
    {
      component: isJoyViewOrUrbanOasisAllInclusive && (
        <div
          className="flex items-center justify-between rounded-12 border-1 border-grey bg-pearl p-15"
          data-testid="joyview-frame"
        >
          <div className="flex items-center gap-20">
            <div>
              <Icon className="text-black" name={ICONS.ALL_INCLUSIVE} width="1.875rem" />
            </div>
            <p className="font-bold">{t('booking.services.PAIBlockTitle')}</p>
          </div>
          <p className="font-bold text-sienna">
            {selectedProposal.alternativePackages &&
              selectedProposal.alternativePackages[0].upsellPrice}
          </p>
        </div>
      ),
      isNull: !isJoyViewOrUrbanOasisAllInclusive,
    },
    {
      component: isUrbanOasisFoodAndBeverage && (
        <div
          className="flex items-center justify-between rounded-12 border-1 border-grey bg-pearl p-15"
          data-testid="joyview-frame"
        >
          <div className="flex items-center gap-20">
            <div>
              <Icon className="text-black" name={ICONS.DISHES} width="1.875rem" />
            </div>
            <p className="font-bold">{t('booking.services.FFBBlockTitle')}</p>
          </div>
          <p className="font-bold text-sienna">
            {selectedProposal.alternativePackages &&
              selectedProposal.alternativePackages[0].upsellPrice}
          </p>
        </div>
      ),
      isNull: !isUrbanOasisFoodAndBeverage,
    },
  ];
  return lines;
};
